import deepmerge from "deepmerge";
import { merge } from "lodash";
import { convertGetMonitorInfo, getPlacementSettings, FINSEMBLEDIMENSIONMEMORY, convertFinsembleLaunchGroupAppd2GlueGroupOptions, getGdDisplayIndexWithMine, adjustPlacementBounds, FSBLPromise, getConfigurationAndCache } from "../common/windowFunctions";
import { asyncMap, asyncMapSequential, getMyWindowIdentifier, reveal, minimizeAll, spawnParamsToStartOptions, toGoLang, toGoLangInvoke, glueDefinitionToAppD, glueWindowToWindowDescriptor, getComponentList } from "../common/windowFunctions";
import { FinsembleWindowImpl } from "../finsembleWindow";
import { logStatus } from "../utils";
const okayToOpenMenu = {};
export const getDesktopBoundingBox = async (glue, element) => {
    const el = typeof element === "string" ? document.querySelector(element) : element;
    if (!el) {
        console.error("getDesktopBoundingBox can't find element", element);
    }
    const box = el ? el.getBoundingClientRect() : { top: 0, left: 0, width: 0, height: 0 };
    const bounds = await glue.windows.my()?.getBounds();
    const boundsTop = bounds?.top ?? 0;
    const boundsLeft = bounds?.left ?? 0;
    const boundingBox = {
        top: boundsTop + box.top,
        left: boundsLeft + box.left,
        width: box.width,
        height: box.height,
        right: 0,
        bottom: 0,
    };
    boundingBox.right = boundingBox.left + boundingBox.width;
    boundingBox.bottom = boundingBox.top + boundingBox.height;
    return boundingBox;
};
export const isPointInBox = (point, box) => {
    if (!box.bottom) {
        box.bottom = box.top + box.height;
    }
    if (!box.right) {
        box.right = box.left + box.width;
    }
    return point.x > box.left && point.x < box.right && point.y < box.bottom && point.y > box.top;
};
export const isMouseOverDOMElement = async (glue, element) => {
    const boundingBox = await getDesktopBoundingBox(glue, element);
    const position = await glue.displays.getMousePosition();
    return isPointInBox(position, boundingBox);
};
export class AppsClientImpl {
    constructor(gluePromise, asyncConstructors, routerClient, loggerClient) {
        this.asyncConstructors = asyncConstructors;
        this.routerClient = routerClient;
        this.loggerClient = loggerClient;
        const pw = asyncConstructors.addConstructor();
        gluePromise.then((g) => {
            this.glue = g;
            asyncConstructors.finishConstructor(pw);
        });
        this.bringWindowsToFront = this.bringWindowsToFront.bind(this);
        this.minimizeWindows = this.minimizeWindows.bind(this);
    }
    async getInstance(windowName, instance) {
        try {
            if (instance) {
                this.logStatus("spawn", "debug", "waiting for instance.getWindow()");
                await instance.getWindow();
            }
            const { wrap } = await FinsembleWindowImpl.getInstance({ windowName });
            return wrap;
        }
        catch (e) {
            this.logStatus("getInstance", "error", e.toString());
            return null;
        }
    }
    ;
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "AppsClient",
            method, type, data
        });
    }
    async addUserDefinedComponent(params, cb) {
        const definition = {
            type: "window",
            name: params.name,
            title: params.name,
            details: {
                url: params.url
            },
            customProperties: {
                isUserDefined: true
            }
        };
        const { err } = await toGoLang(() => this.glue.appManager.inMemory.import([definition], "merge"));
        if (!err) {
            const update = {
                [params.name]: params
            };
            await toGoLang(() => this.glue.prefs.updateFor("IO.Global.UserDefinedApps", update));
        }
        cb?.(err);
        return { err };
    }
    async removeUserDefinedComponent(params, cb) {
        const { err } = await toGoLang(() => this.glue.appManager.inMemory.remove(params.name));
        if (!err) {
            const { data: preferences } = await toGoLang(() => this.glue.prefs.get("IO.Global.UserDefinedApps"));
            const userDefinedApps = preferences?.data ?? {};
            delete userDefinedApps[params.name];
            await toGoLang(() => this.glue.prefs.setFor("IO.Global.UserDefinedApps", userDefinedApps));
        }
        cb?.(err);
        return { err };
    }
    async getApps(appIds, cb) {
        this.logStatus("getApps", "start", { appIds });
        await this.asyncConstructors.ready();
        const { err, data: definitions } = await toGoLang(() => this.glue.appManager.getConfigurations());
        if (err || !definitions) {
            const error = `AppsClient.getApps:getConfigurations ${err}`;
            this.logStatus("getApps", "error", { appIds });
            cb?.(error);
            return { err: error };
        }
        const finsembleApplications = definitions.flatMap((definition) => {
            if (definition?.customProperties?.component) {
                return [];
            }
            const appdRecord = deepmerge(glueDefinitionToAppD(definition) ?? {}, definition?.customProperties?.appd ?? {});
            return [appdRecord];
        });
        const data = finsembleApplications.filter((app) => !appIds || appIds.includes(app.appId));
        this.logStatus("getApps", "return", { err: null, data });
        cb?.(null, data);
        return {
            err: null,
            data
        };
    }
    async getComponentList(cb) {
        this.logStatus("getComponentList", "start");
        await this.asyncConstructors.ready();
        const { err, data } = await getComponentList(this.glue);
        this.logStatus("getComponentList", "return", { err, data });
        cb?.(err, data);
        return {
            err,
            data
        };
    }
    async addApps(apps, cb) {
        this.logStatus("addApps", "start", { apps });
        await this.asyncConstructors.ready();
        const { err: err2 } = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.AppsClient.addApps", { apps }));
        this.logStatus("addApps", "return", { err: err2, data: apps });
        cb?.(err2, apps);
        return {
            err: err2,
            data: apps
        };
    }
    async removeApps(appIds, cb) {
        this.logStatus("removeApps", "start", { appIds });
        await this.asyncConstructors.ready();
        const promises = appIds.map((appId) => this.glue.appManager.inMemory.remove(appId));
        const results = await FSBLPromise().allSettled(promises);
        const errors = results.flatMap((result) => result.status === "rejected" ? [result.reason] : []);
        const err = errors.length ? errors.join(" ") : null;
        await this.glue.interop.invoke("FSBL.Clients.AppsClient.removeApps", { appIds });
        this.logStatus("removeApps", "return", { err, data: appIds });
        cb?.(err, appIds);
        return {
            err,
            data: appIds
        };
    }
    async getActiveDescriptors(cb) {
        this.logStatus("getActiveDescriptors", "start");
        await this.asyncConstructors.ready();
        const windows = this.glue.windows.list();
        const configs = await this.glue.appManager.getConfigurations();
        const descriptors = windows.map((win) => glueWindowToWindowDescriptor(win, configs));
        const data = Object.fromEntries(descriptors.map((descriptor) => [descriptor.name, descriptor]));
        this.logStatus("getActiveDescriptors", "return", { err: null, data });
        cb?.(null, data);
        return {
            err: null,
            data
        };
    }
    async getMyWindowIdentifier(cb) {
        this.logStatus("getMyWindowIdentifier", "start");
        await this.asyncConstructors.ready();
        const wi = await getMyWindowIdentifier(this.glue);
        this.logStatus("getMyWindowIdentifier", "return", wi);
        cb?.(wi);
        return wi;
    }
    async bringWindowsToFront(params, cb) {
        await this.asyncConstructors.ready();
        return await reveal(this.glue, params, cb);
    }
    async minimizeWindows(params, cb) {
        await this.asyncConstructors.ready();
        return await minimizeAll(this.glue, params, cb);
    }
    async spawnTabCollection({ componentList, globalParams, }, cb) {
        this.logStatus("spawnTabCollection", "start", { componentList, globalParams });
        await this.asyncConstructors.ready();
        let tabGroupId = null;
        const results = await asyncMapSequential(componentList, async (app) => {
            const application = this.glue.appManager.application(app.appID);
            if (!application) {
                return { err: `Application ${app.appID} not found`, data: null };
            }
            const config = await this.glue.appManager.getConfigurations([app.appID]);
            const applicationOptions = await spawnParamsToStartOptions(this.glue, merge({}, app.individualParams ?? {}, globalParams ?? {}), config[0] ?? {});
            if (tabGroupId) {
                applicationOptions.tabGroupId = tabGroupId;
            }
            const spawnData = app.individualParams?.options?.customData ?? app.individualParams?.data;
            const context = spawnData ? {
                "#finsemble-spawnData": spawnData
            } : {};
            const w = await application.start(context, applicationOptions);
            tabGroupId = w.window.tabGroupId;
            return { err: null, data: w };
        });
        const data = {
            id: results?.[0]?.data?.id ?? "",
            windows: await asyncMap(results, async (result) => {
                const finWindow = await this.getInstance(result.data?.id, result.data);
                return {
                    windowDescriptor: {
                        name: result.data?.id ?? ""
                    },
                    windowIdentifier: {
                        windowName: result.data?.id ?? "",
                        componentType: result.data?.window.name
                    },
                    finWindow: finWindow
                };
            })
        };
        this.logStatus("spawnTabCollection", "return", { err: null, data });
        cb?.(null, data);
        return {
            err: null,
            data
        };
    }
    async clone(toClone, spawnParams, cb) {
        this.logStatus("clone", "start", { toClone, spawnParams });
        await this.asyncConstructors.ready();
        const win = toClone ? this.glue.windows.findById(toClone.windowName) : this.glue.windows.my();
        if (!win) {
            const error = "Attempt to clone incompatible window type (called from webgroup?)";
            this.logStatus("clone", "error", error);
            cb?.(error);
            return { error };
        }
        try {
            const newWin = await win.clone();
            this.logStatus("clone", "return");
            const finWindow = await this.getInstance(newWin.id);
            const response = {
                windowDescriptor: {
                    name: newWin.id
                },
                windowIdentifier: {
                    windowName: newWin.id,
                    componentType: newWin.name
                },
                finWindow: finWindow
            };
            cb?.(null, response);
            return {
                error: null,
                response
            };
        }
        catch (e) {
            const error = e.toString();
            this.logStatus("clone", "error", error);
            cb?.(error);
            return {
                error
            };
        }
    }
    async maybeSpawnLaunchGroup(component, params, config) {
        if (!config) {
            return { didSpawn: false };
        }
        const isLaunchGroup = config.customProperties?.appd?.hostManifests?.Finsemble?.window?.windowType === "launchGroup";
        if (!isLaunchGroup) {
            return { didSpawn: false };
        }
        const { err, data: glueOptions } = await convertFinsembleLaunchGroupAppd2GlueGroupOptions(this.glue, config.customProperties?.appd);
        if (err) {
            this.loggerClient.error(err);
        }
        if (!glueOptions) {
            return { didSpawn: false };
        }
        this.loggerClient.debug("maybeSpawnLaunchGroup CreateGroupOptions", glueOptions);
        const { err: error, data } = await toGoLang(() => this.glue.windows.groups.create(glueOptions));
        if (error || !data) {
            this.loggerClient.error("maybeSpawnLaunchGroup", error, glueOptions);
            return {
                didSpawn: false,
                error: error ?? "No data returned"
            };
        }
        const instance = data[0];
        const finWindow = await this.getInstance(instance.id);
        return {
            didSpawn: true,
            error,
            response: {
                launchGroupWindows: instance.windows.map(({ id }) => id),
                finWindow: finWindow,
                windowDescriptor: {
                    name: instance.id,
                },
                windowIdentifier: {
                    windowName: instance.id,
                    componentType: component
                }
            }
        };
    }
    async spawn(component, params, cb) {
        this.logStatus("spawn", "start", { component, params });
        await this.asyncConstructors.ready();
        const config = await this.glue.appManager.getConfigurations([component]);
        const { didSpawn, ...rest } = await this.maybeSpawnLaunchGroup(component, params, config[0] ?? {});
        if (didSpawn) {
            cb?.(rest.error, rest.response);
            return rest;
        }
        const { err, data: application } = await toGoLang(() => this.glue.appManager.application(component));
        if (err || !application) {
            const error = `AppsClient.spawn:application ${err}`;
            this.logStatus("spawn", "error", error);
            console.log(error);
            cb?.(error);
            return { error };
        }
        const applicationOptions = await spawnParamsToStartOptions(this.glue, params, config[0] ?? {});
        applicationOptions.awaitInterop = false;
        const { data: myConfig } = await toGoLang(() => getConfigurationAndCache(this.glue.windows.my())) ?? {};
        const autoAssociateEntry = (myConfig?.customProperties?.appd?.hostManifests?.Finsemble?.interop?.selectConnect ?? []).find((entry) => entry.autoAssociate);
        if (autoAssociateEntry) {
            const myCurrentChannel = await this.glue.channels.current();
            if (autoAssociateEntry.autoAssociate.allChildren || autoAssociateEntry.autoAssociate.selectChildren?.includes(component)) {
                applicationOptions.channelId = myCurrentChannel;
            }
        }
        const spawnData = merge(config[0]?.customProperties?.appd?.hostManifests?.Finsemble?.window?.data ?? {}, params?.data ?? params?.options?.customData ?? {});
        const componentState = params?.componentState;
        const context = spawnData ? {
            "#finsemble-spawnData": spawnData
        } : {};
        if (componentState) {
            context["#finsemble-componentState"] = componentState;
        }
        if (params.name) {
            context["#finsemble-windowName"] = params.name;
        }
        const { err: err2, data: instance } = await toGoLang(() => application.start(context, applicationOptions));
        if (err2 || !instance) {
            const error = `AppsClient.spawn:start ${err2}`;
            this.logStatus("spawn", "error", error);
            cb?.(error);
            return { error };
        }
        const isAssimilatedWindow = config[0]?.customProperties?.appd?.hostManifests?.Finsemble?.window?.windowType === "assimilation";
        const finWindow = isAssimilatedWindow ? null : await this.getInstance(instance.id, instance);
        const response = {
            windowDescriptor: {
                name: instance.id
            },
            windowIdentifier: {
                windowName: instance.id,
                componentType: component
            },
            finWindow: finWindow
        };
        this.logStatus("spawn", "return", { error: null, response });
        cb?.(null, response);
        return {
            error: null,
            response
        };
    }
    async toggleWindowOnClick(element, windowLocator, params) {
        this.logStatus("toggleWindowOnClick", "start", { element, windowLocator, params });
        const windowName = "windowName" in windowLocator ? windowLocator.windowName : windowLocator;
        const componentType = "componentType" in windowLocator ? windowLocator.componentType : null;
        const windowMatcher = (w) => (typeof windowName === "string") ? (windowName === w.id || windowName === w.name || windowName === w.title) : componentType === w.name;
        const w = this.glue.windows.list().find(windowMatcher);
        if (!w) {
            const errors = ["AppsClient.toggleWindowOnClick() cannot find window", windowLocator];
            this.logStatus("toggleWindowOnClick", "error", ...errors);
            return;
        }
        const key = w.id;
        if (okayToOpenMenu[key] === false) {
            okayToOpenMenu[key] = true;
            return;
        }
        if (!params.position) {
            params.position = "absolute";
        }
        const { err } = await this.showWindow(windowLocator, params);
        if (err) {
            const errors = ["AppsClient.toggleWindowOnClick() cannot show window", err];
            this.logStatus("toggleWindowOnClick", "error", ...errors);
            return;
        }
        w.onFocusChanged(async () => {
            if (w.isFocused) {
                return;
            }
            okayToOpenMenu[key] = true;
            const mouseIsOverElement = await isMouseOverDOMElement(this.glue, element);
            okayToOpenMenu[key] = !mouseIsOverElement;
        });
        this.logStatus("toggleWindowOnClick", "return");
    }
    async showWindow(windowLocator, params = {}, cb) {
        this.logStatus("showWindow", "start", { windowLocator, params });
        await this.asyncConstructors.ready();
        const windowName = "windowName" in windowLocator ? windowLocator.windowName : windowLocator;
        const componentType = "componentType" in windowLocator ? windowLocator.componentType : null;
        const windowMatcher = (w) => (typeof windowName === "string") ? (windowName === w.id || windowName === w.name || windowName === w.title) : componentType === w.name;
        const w = this.glue.windows.list().find(windowMatcher);
        if (!w) {
            this.loggerClient.debug(`AppsClient.showWindow(): Window ${windowName} not found. Spawning.`);
            if (componentType) {
                const { response, error } = await this.spawn(componentType, params, cb);
                this.logStatus("showWindow", "return", { err: error, data: response });
                return { err: error, data: response };
            }
            const err = `Window ${windowName} type ${componentType} not found`;
            this.loggerClient.warn("AppsClient.toggleWindowOnClick", { err });
            return { err };
        }
        const gdDisplayIndex = await getGdDisplayIndexWithMine(this.glue, params?.monitor);
        const placement = getPlacementSettings(this.glue, params, gdDisplayIndex);
        if (!placement) {
            const err = "Error, can't place window";
            this.loggerClient.error("AppsClient.toggleWindowOnClick", { err, params });
            return { err };
        }
        const dimensions = await this.glue.contexts.get(FINSEMBLEDIMENSIONMEMORY);
        const previousDimensions = dimensions?.[w.id] ?? await w.getBounds();
        if (!dimensions?.[w.id]) {
            const { err } = await toGoLang(() => this.glue.contexts.update(FINSEMBLEDIMENSIONMEMORY, {
                [w.id]: previousDimensions
            }));
            if (err) {
                this.logStatus("showWindow", "error", { subFunction: "this.glue.contexts.update(FINSEMBLEDIMENSIONMEMORY)", err: err.toString() });
            }
        }
        await adjustPlacementBounds(this.glue, placement, gdDisplayIndex);
        placement.width = placement.width ?? previousDimensions?.width;
        placement.height = placement.height ?? previousDimensions?.height;
        await w.place(placement);
        if (!params.setBoundsOnly) {
            await w.show();
            await w.focus();
        }
        const finWindow = await this.getInstance(w.id);
        const data = {
            windowDescriptor: {
                name: w.id
            },
            windowIdentifier: {
                windowName: w.id
            },
            finWindow: finWindow
        };
        this.logStatus("showWindow", "return", { err: null, data });
        cb?.(null, data);
        return {
            err: null,
            data
        };
    }
    async getMonitorInfo(params = {}, cb) {
        this.logStatus("getMonitorInfo", "start", { params });
        await this.asyncConstructors.ready();
        let glueDisplay = null;
        if (params?.windowIdentifier?.windowName) {
            const glueWindow = this.glue.windows.findById(params.windowIdentifier.windowName);
            glueDisplay = await glueWindow?.getDisplay();
        }
        else if (params?.monitor === "primary") {
            glueDisplay = (await this.glue.displays.all()).find((d) => d.isPrimary);
        }
        else if (typeof (params?.monitor) === "number") {
            glueDisplay = (await this.glue.displays.all()).find((d) => d.index === params.monitor);
        }
        else if (params?.monitor === "previous") {
            const currentDisplay = await this.glue.windows.my()?.getDisplay();
            glueDisplay = (await this.glue.displays.all()).find((d) => d.index - 1 === (currentDisplay?.index ?? 0));
        }
        else if (params?.monitor === "next") {
            const currentDisplay = await this.glue.windows.my()?.getDisplay();
            glueDisplay = (await this.glue.displays.all()).find((d) => d.index + 1 === (currentDisplay?.index ?? 0));
        }
        else if (params?.monitor === "mine" || !params?.windowIdentifier?.windowName) {
            glueDisplay = await this.glue.windows.my()?.getDisplay();
        }
        const finsembleMonitor = glueDisplay ? convertGetMonitorInfo(glueDisplay) : null;
        if (!finsembleMonitor) {
            const err = "Cannot fetch monitor. Check application.log for errors";
            this.logStatus("getMonitorInfo", "error", err);
            cb?.(err);
            return { err };
        }
        this.logStatus("getMonitorInfo", "return", { err: null, data: finsembleMonitor });
        cb?.(null, finsembleMonitor);
        return {
            err: null,
            data: finsembleMonitor
        };
    }
    async getMonitorInfoAll(cb) {
        this.logStatus("getMonitorInfoAll", "start");
        await this.asyncConstructors.ready();
        const glueDisplays = await this.glue.displays.all();
        const finsembleMonitors = glueDisplays.map((d) => convertGetMonitorInfo(d));
        const finsembleMonitorsNoNulls = finsembleMonitors.filter((f) => f !== null);
        if (finsembleMonitors.length !== finsembleMonitorsNoNulls.length) {
            const err = "Cannot fetch some monitors. Check application.log for errors";
            this.logStatus("getMonitorInfoAll", "error", err);
            cb?.(err);
            return { err };
        }
        this.logStatus("getMonitorInfoAll", "return", { err: null, data: finsembleMonitorsNoNulls });
        cb?.(null, finsembleMonitorsNoNulls);
        return {
            err: null,
            data: finsembleMonitorsNoNulls
        };
    }
    async getComponentDefaultConfig(componentType, cb) {
        this.logStatus("getComponentDefaultConfig", "start", { componentType });
        await this.asyncConstructors.ready();
        const { err, data: configs } = await this.getComponentList();
        let data = undefined;
        if (!err && configs) {
            const entries = Object.entries(configs).find(([key]) => key === componentType);
            if (entries) {
                data = entries[1];
            }
        }
        this.logStatus("getComponentDefaultConfig", "return", { err, data });
        cb?.(err, data);
        return {
            err,
            data
        };
    }
}
